import React, { useState } from 'react';
import { Client, ClientTranscript, ClientFormData } from '../../types/client';
import { useClients } from '../../hooks/useClients';
import { useClientTranscripts } from '../../hooks/useClientTranscripts';
import ClientList from './ClientList';
import ClientTranscriptsList from './ClientTranscriptsList';
import TranscriptDetails from './TranscriptDetails';
import ClientForm from './ClientForm';
import ClientAnalytics from './ClientAnalytics';

const ClientsPage: React.FC = () => {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedTranscript, setSelectedTranscript] = useState<ClientTranscript | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(true);

  // Use custom hooks for data management
  const {
    clients,
    loading: clientsLoading,
    loadingMore: clientsLoadingMore,
    error: clientsError,
    pagination: clientsPagination,
    fetchClients,
    handleLoadMore: handleLoadMoreClients,
    deleteClient
  } = useClients();

  const {
    transcripts: clientTranscripts,
    loading: transcriptsLoading,
    loadingMore: transcriptsLoadingMore,
    pagination: transcriptsPagination,
    fetchTranscripts: fetchClientTranscripts,
    handleLoadMore: handleLoadMoreTranscripts,
    resetTranscripts
  } = useClientTranscripts();

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setSelectedTranscript(null);
    resetTranscripts();
    fetchClientTranscripts(client.id);
  };

  const handleTranscriptSelect = (transcript: ClientTranscript) => {
    setSelectedTranscript(transcript);
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowForm(true);
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDeleteClient = async (clientId: string) => {
    await deleteClient(clientId);
    if (selectedClient?.id === clientId) {
      setSelectedClient(null);
      setSelectedTranscript(null);
      resetTranscripts();
    }
  };

  const handleFormSubmit = async (formData: ClientFormData) => {
    try {
      const isEditing = editingClient !== null && formData.id;
      
      const response = await fetch('/api/clients/save', {
        method: isEditing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchClients();
        setShowForm(false);
      } else {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Failed to save client');
      }
    } catch (err) {
      console.error('Error saving client:', err);
      alert(`Failed to save client: ${err instanceof Error ? err.message : 'Please try again.'}`);
    }
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  const handleLoadMoreClientsWrapper = () => {
    handleLoadMoreClients();
  };

  const handleLoadMoreTranscriptsWrapper = () => {
    if (selectedClient) {
      handleLoadMoreTranscripts(selectedClient.id);
    }
  };

  // Loading state
  if (clientsLoading) {
    return (
      <div className="bg-gray-50 flex flex-col h-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (clientsError) {
    return (
      <div className="bg-gray-50 flex flex-col h-full items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Clients</h3>
          <p className="text-gray-600 mb-4">{clientsError}</p>
          <button
            onClick={() => fetchClients()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 flex flex-col h-full">
      {/* Header Section with Analytics */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => window.location.href = '/'}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Go to homepage"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10M9 21h6" />
                  </svg>
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
              </div>
              <p className="text-gray-600">Manage your client relationships and conversations</p>
            </div>
            
            {/* Analytics Toggle Button */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm font-medium"
              title={showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
            >
              <span>{showAnalytics ? '📊' : '📈'}</span>
              <span>{showAnalytics ? 'Hide Analytics' : 'Show Analytics'}</span>
              <svg 
                className={`w-4 h-4 transition-transform ${showAnalytics ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Collapsible Client Analytics Dashboard */}
        {showAnalytics && (
          <div className="px-6 pb-6">
            <ClientAnalytics />
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Client List */}
        <ClientList
          clients={clients}
          selectedClient={selectedClient}
          searchTerm={searchTerm}
          loading={clientsLoading}
          loadingMore={clientsLoadingMore}
          hasMore={clientsPagination.hasMore}
          totalClients={clientsPagination.total}
          onClientSelect={handleClientSelect}
          onSearchChange={handleSearchChange}
          onCreateClient={handleCreateClient}
          onLoadMore={handleLoadMoreClientsWrapper}
        />

        {/* Middle Column - Client Transcripts */}
        <ClientTranscriptsList
          selectedClient={selectedClient}
          transcripts={clientTranscripts}
          selectedTranscript={selectedTranscript}
          loading={transcriptsLoading}
          loadingMore={transcriptsLoadingMore}
          hasMore={transcriptsPagination.hasMore}
          totalTranscripts={transcriptsPagination.total}
          onTranscriptSelect={handleTranscriptSelect}
          onEditClient={handleEditClient}
          onDeleteClient={handleDeleteClient}
          onLoadMore={handleLoadMoreTranscriptsWrapper}
        />

        {/* Right Column - Transcript Details */}
        <TranscriptDetails selectedTranscript={selectedTranscript} />
      </div>

      {/* Client Form Modal */}
      {showForm && (
        <ClientForm
          isOpen={showForm}
          editingClient={editingClient}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default ClientsPage; 
